import React from 'react';
import PropTypes from 'prop-types';
import { FormattedTime } from 'react-intl';
import { getTimeFromHour } from '../../../../modules/datetime';
import { compose, pure } from '../../../../src/web/utils/react';
import useCurrentPage from '../../../hooks/useCurrentPage';
import Translation from '../Translation';
import styles from './HoursTable.scss';

const propTypes = {
  title: PropTypes.node.isRequired,
  note: PropTypes.string,
  hours: PropTypes.arrayOf(PropTypes.shape({
    day: PropTypes.string.isRequired,
    open: PropTypes.number.isRequired,
    close: PropTypes.number.isRequired,
  })),
};

const defaultProps = {
  note: undefined,
  hours: undefined,
};

const daysOfTheWeek = {
  Sunday: { id: 'weekday.sunday', defaultMessage: 'Sunday' },
  Monday: { id: 'weekday.monday', defaultMessage: 'Monday' },
  Tuesday: { id: 'weekday.tuesday', defaultMessage: 'Tuesday' },
  Wednesday: { id: 'weekday.wednesday', defaultMessage: 'Wednesday' },
  Thursday: { id: 'weekday.thursday', defaultMessage: 'Thursday' },
  Friday: { id: 'weekday.friday', defaultMessage: 'Friday' },
  Saturday: { id: 'weekday.saturday', defaultMessage: 'Saturday' },
};

const makeBin = ({ day, open, close }) => ({ days: [day], open, close });

function rollUpHours(hours) {
  // huge assumption here: the hours always represent all 7 days, are contiguous
  // and element 0 always represents Sunday. We should be able to guarantee this
  // in the ETL.

  // The goal is to bin each hour with similar neighbors. The first bin will start
  // with the first hour to get the ball rolling
  const bins = [
    makeBin(hours[0]),
  ];
  let cursor = 0;

  // Iterate and bin the remaining hours
  hours.slice(1).forEach((hour) => {
    const { day, open, close } = hour;
    const { open: cursorOpen, close: cursorClose } = bins[cursor];

    if (cursorOpen === open && cursorClose === close) {
      // This hour fits into the bucket under our cursor
      bins[cursor].days.push(day);
    } else {
      // This hour doesn't fit, so let's make a new bucket and move the cursor
      cursor += 1;
      bins[cursor] = makeBin(hour);
    }
  });

  const today = Object.keys(daysOfTheWeek)[(new Date()).getDay()];

  // Finally, convert the bins into something more useful
  return bins.map(({ days, open, close }) => {
    let openHours;
    if (open === -1 || close === -1) {
      openHours = <Translation id="widgets.hours.hoursTable.closed" defaultMessage="Closed" />;
    } else if (open === close) {
      openHours = <Translation id="widgets.hours.hoursTable.open24Hours" defaultMessage="Open 24 Hours" />;
    } else {
      openHours = (
        <React.Fragment>
          <FormattedTime value={getTimeFromHour(open)} />
          {' - '}
          <FormattedTime value={getTimeFromHour(close)} />
        </React.Fragment>
      );
    }

    const openingDay = days[0];
    const closingDay = days.slice(1).pop();

    const daysComponents = (
      <React.Fragment>
        {openingDay && <Translation {...daysOfTheWeek[openingDay]} />}
        {openingDay && closingDay && ' - '}
        {closingDay && <Translation {...daysOfTheWeek[closingDay]} />}
      </React.Fragment>
    );

    return {
      days: daysComponents,
      openHours,
      isToday: days.indexOf(today) !== -1,
    };
  });
}

const HoursTable = ({ title, note, hours }) => {
  if (!hours) {
    return false;
  }

  const groups = rollUpHours(hours);
  const {
    primaryColorClass,
    tertiaryColorBackgroundClass,
  } = useCurrentPage();

  return (
    <div>
      <h3 className={`${styles.sectionHeader} ${primaryColorClass}`}>{title}</h3>
      <table className={styles.hoursTable}>
        <tbody>
          {groups.map(({ days, openHours, isToday }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index} className={isToday ? tertiaryColorBackgroundClass : ''}>
              <td className={styles.days}>{days}</td>
              <td className={styles.openHours}>{openHours}</td>
            </tr>
          ))
          }
        </tbody>
      </table>
      <p><b>{note}</b></p>
    </div>
  );
};

HoursTable.propTypes = propTypes;
HoursTable.defaultProps = defaultProps;

export default compose(
  pure(),
)(HoursTable);
